import type { Metric } from './FormTrends.types.ts';

import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import { WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from '../Base.tsx';
import { useFormWidget } from '../use-form-widget.ts';
import { CombinedView } from './CombinedView.tsx';
import { ComparisonView } from './ComparisonView.tsx';
import { useFormTrendsStore } from './FormTrends.store.ts';

const widgetTitle = 'Form Trends';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Tracks trends in form activity over time—how many forms were assigned,
        completed, or canceled—with options to filter by&nbsp;
        <InfoEmphasis>required only</InfoEmphasis>
        ,&nbsp;
        <InfoEmphasis>optional only</InfoEmphasis>, or&nbsp;
        <InfoEmphasis>all forms</InfoEmphasis>. This helps identify bottlenecks
        in patient preparedness, evaluate engagement with optional forms, and
        assess staff workflow efficiency.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <Box sx={{ pl: 5 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Forms – Combined View
        </Typography>
        <Typography variant="body2">
          Shows three metrics side by side for the selected date range:
        </Typography>
      </Box>
      <InfoList
        items={[
          <>
            <InfoEmphasis>Assigned</InfoEmphasis>: Number of forms that were
            assigned (excludes canceled and deleted forms)
          </>,
          <>
            <InfoEmphasis>Completed</InfoEmphasis>: Number of forms that were
            completed (includes canceled and deleted forms if they also have a
            completion timestamp)
          </>,
          <>
            <InfoEmphasis>Canceled</InfoEmphasis>: Number of forms that were
            canceled or deleted (does not count incomplete forms as canceled)
          </>,
        ]}
      />
      <Box sx={{ pl: 5 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Forms – Required vs. Optional View
        </Typography>
        <Typography variant="body2">
          Focuses on one metric at a time (Assigned, Completed, or Canceled),
          split into two lines:
        </Typography>
      </Box>
      <InfoList
        items={[
          <>
            <InfoEmphasis>Required Forms</InfoEmphasis>: Forms marked as
            required
          </>,
          <>
            <InfoEmphasis>Optional Forms</InfoEmphasis>: Forms not required
          </>,
        ]}
      />

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Forms with a valid assigned, completed, or canceled timestamp relevant to the selected metric',
            'Forms matching the selected filter: Required only, Optional only, or All forms',
            'Deleted forms (in Completed and Canceled counts)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={[
            'Canceled and deleted forms (from Assigned counts)',
            'Forms missing all relevant timestamps for the selected metric',
          ]}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            '“Assigned” counts are based only on when the form was assigned, and exclude canceled or deleted forms',
            '“Completed” counts may include forms that were later canceled or deleted',
            '“Canceled” counts include canceled and deleted forms, but do not include incomplete forms',
            'Completed and canceled counts may include forms that were assigned in an earlier time period',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function FormTrends() {
  const widget = useFormWidget();
  const params = widget.getCommonRequestParams();

  const formTrendsStore = useFormTrendsStore();

  return (
    /**
     * As of TS-15605, the data fetching was moved to the two view components,
     * which means that we don't have access here to the tooltip text as it
     * comes from the API. This is fine for the moment given we'll rework all
     * tooltips as part of TS-15238. Depending on how rich the new contents
     * are, we might have to hard-code them here, so keeping the data fetching
     * in the views is totally fine, otherwise we'll have to lift the data
     * fetching logic to this component.
     */
    <Base
      title={widgetTitle}
      description="Form Priority filter is ignored in this chart"
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath((searchParams) => {
        // Since this widget ignores the global value, we must always load all
        // required and optional forms when opening the drilldown.
        searchParams.delete('fp');
        return searchParams;
      })}
    >
      {(isOpenedInDialog) => (
        <Stack
          direction="column"
          spacing={2}
          height={isOpenedInDialog ? '100%' : undefined}
        >
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <FormControl>
              <RadioGroup
                value={formTrendsStore.view}
                onChange={(event) => {
                  if (event.target.value === 'combined') {
                    formTrendsStore.setViewState({ view: 'combined' });
                  } else {
                    formTrendsStore.setViewState({
                      view: 'comparison',
                      metric: formTrendsStore.metric ?? 'completed',
                    });
                  }
                }}
                aria-label="Form trends view mode"
              >
                <FormControlLabel
                  value="combined"
                  control={<Radio />}
                  label="Combined"
                />
                <FormControlLabel
                  value="comparison"
                  control={<Radio />}
                  label="Required vs Optional"
                />
              </RadioGroup>
            </FormControl>

            {formTrendsStore.view === 'comparison' && (
              <TextField
                select
                size="small"
                label="Metric"
                value={formTrendsStore.metric}
                onChange={(event) => {
                  formTrendsStore.setViewState({
                    view: 'comparison',
                    metric: event.target.value as Metric,
                  });
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
              </TextField>
            )}
          </Stack>

          <Box height={isOpenedInDialog ? '100%' : WIDGET_CHART_HEIGHT}>
            {formTrendsStore.view === 'combined' ? (
              <CombinedView params={params} />
            ) : (
              <ComparisonView metric={formTrendsStore.metric} params={params} />
            )}
          </Box>
        </Stack>
      )}
    </Base>
  );
}
